import React, { useState, useRef } from 'react';
import {
  Box,
  Button,
  Avatar,
  Typography,
  Alert
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  Image as ImageIcon
} from '@mui/icons-material';
import ImageCropDialog from './ImageCropDialog';
import { logger } from '../../utils/logger';

interface ImageUploadWithCropProps {
  value: string | null;
  onChange: (imageFile: File | null, imageUrl: string | null) => void;
  label?: string;
  helperText?: string;
  maxSizeInMB?: number;
  outputSize?: { width: number; height: number };
  aspectRatio?: number;
  disabled?: boolean;
}

const ImageUploadWithCrop: React.FC<ImageUploadWithCropProps> = ({
  value,
  onChange,
  label = 'Slika člana',
  helperText = 'Preporučena veličina: 512x512px',
  maxSizeInMB = 5,
  outputSize = { width: 512, height: 512 },
  aspectRatio = 1,
  disabled = false
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [tempImageUrl, setTempImageUrl] = useState<string | null>(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    if (!file) return;

    // Reset error
    setUploadError(null);

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setUploadError('Molimo izaberite sliku (PNG, JPG, JPEG)');
      return;
    }

    // Validate file size before cropping
    if (file.size > maxSizeInMB * 1024 * 1024) {
      setUploadError(`Slika je prevelika. Maksimalna veličina je ${maxSizeInMB}MB`);
      return;
    }

    // Create temporary URL for cropping
    const imageUrl = URL.createObjectURL(file);
    setTempImageUrl(imageUrl);
    setCropDialogOpen(true);
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    try {
      // Convert blob to File
      const croppedFile = new File(
        [croppedBlob],
        `cropped_${Date.now()}.jpg`,
        { type: 'image/jpeg' }
      );

      // Create URL for preview
      const croppedUrl = URL.createObjectURL(croppedBlob);

      // Call parent onChange with file and URL
      onChange(croppedFile, croppedUrl);

      // Cleanup
      if (tempImageUrl) {
        URL.revokeObjectURL(tempImageUrl);
      }
      setTempImageUrl(null);
      setCropDialogOpen(false);

      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    } catch (error) {
      logger.error('Error processing cropped image:', error);
      setUploadError('Greška pri obradi slike');
    }
  };

  const handleCropCancel = () => {
    if (tempImageUrl) {
      URL.revokeObjectURL(tempImageUrl);
    }
    setTempImageUrl(null);
    setCropDialogOpen(false);

    // Reset file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDelete = () => {
    if (value) {
      URL.revokeObjectURL(value);
    }
    onChange(null, null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  return (
    <Box>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
        onChange={handleFileSelect}
        disabled={disabled}
      />

      <Box display="flex" flexDirection="column" gap={2}>
        <Typography variant="subtitle2" color="text.secondary">
          {label}
        </Typography>

        {uploadError && (
          <Alert severity="error" onClose={() => setUploadError(null)}>
            {uploadError}
          </Alert>
        )}

        <Box display="flex" alignItems="center" gap={2}>
          {/* Image Preview */}
          {value ? (
            <Avatar
              src={value}
              sx={{
                width: 120,
                height: 120,
                borderRadius: 2,
                border: '2px solid',
                borderColor: 'primary.main'
              }}
            >
              <ImageIcon sx={{ fontSize: 48 }} />
            </Avatar>
          ) : (
            <Box
              sx={{
                width: 120,
                height: 120,
                borderRadius: 2,
                border: '2px dashed',
                borderColor: 'grey.300',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: 'grey.50'
              }}
            >
              <ImageIcon sx={{ fontSize: 48, color: 'grey.400' }} />
            </Box>
          )}

          {/* Upload/Delete Buttons */}
          <Box display="flex" flexDirection="column" gap={1}>
            <Button
              variant="outlined"
              startIcon={<UploadIcon />}
              onClick={handleUploadClick}
              disabled={disabled}
              size="small"
            >
              {value ? 'Promeni sliku' : 'Upload sliku'}
            </Button>

            {value && (
              <Button
                variant="outlined"
                color="error"
                startIcon={<DeleteIcon />}
                onClick={handleDelete}
                disabled={disabled}
                size="small"
              >
                Obriši
              </Button>
            )}

            <Typography variant="caption" color="text.secondary">
              {helperText}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Crop Dialog */}
      {tempImageUrl && (
        <ImageCropDialog
          open={cropDialogOpen}
          imageUrl={tempImageUrl}
          onClose={handleCropCancel}
          onCropComplete={handleCropComplete}
          aspectRatio={aspectRatio}
          outputSize={outputSize}
        />
      )}
    </Box>
  );
};

export default ImageUploadWithCrop;
